import type { settings as en } from "../en/settings";
import type { Messages } from "../types";

export const settings: Messages<typeof en> = {
  title: "الإعدادات",
  description: "تفضيلاتك الشخصية، وطريقة إعداد مؤسستك.",
  groups: { you: "حسابك", org: "المؤسسة" },
  nav: {
    profile: "الملف الشخصي",
    preferences: "التفضيلات",
    notifications: "الإشعارات",
    general: "عام",
    channels: "القنوات",
    team: "الفريق والأدوار",
    ai: "الذكاء الاصطناعي والمعرفة",
    plan: "الخطة والفوترة",
    api: "الواجهة البرمجية والتكاملات",
    data: "البيانات والخصوصية",
  },
  hints: {
    profile: "اسمك وبريد تسجيل الدخول ودورك.",
    preferences: "اللغة والسمة والكثافة وزر «اسأل».",
    notifications: "ما يصلك، وأين يصلك.",
    general: "مؤسستك وتقويمها.",
    channels: "حسابات إنستغرام المرتبطة وما تستطيعه كل قناة.",
    team: "من يستطيع الاطلاع والعمل في هذه المؤسسة.",
    ai: "ردود الذكاء الاصطناعي والوسوم والفئات الذكية والبحث الذكي.",
    plan: "خطتك، واستخدامك مقارنة بحدودها، والفوترة.",
    api: "الواجهة البرمجية العامة والأحداث الصادرة.",
    data: "مدة الاحتفاظ بالبيانات والحوادث والصفحات القانونية.",
  },
  readOnly: "يمكن للمالكين والمسؤولين فقط تغيير هذه الإعدادات.",
  profile: {
    title: "بياناتك",
    hint: "كيف يراك فريقك.",
    name: "الاسم",
    email: "البريد الإلكتروني",
    emailHint: "بريد تسجيل الدخول الخاص بك.",
    nameHint: "اطلب من مالك أو مسؤول إن احتاج اسمك إلى تغيير.",
    language: "اللغة المفضّلة",
    organization: "المؤسسة",
    role: "دورك",
    photo: "الصورة",
    photoHint: "الأحرف الأولى من اسمك، إلى أن تتوفر الصور.",
    guide: "دليل البدء",
    guideHint: "قائمة في الصفحة الرئيسية ترشدك إلى أول ردودك وقواعدك ومنشوراتك.",
    guideShow: "إظهار دليل البدء",
    guideShown: "عاد الدليل إلى الصفحة الرئيسية",
    guideVisible: "الدليل ظاهر في الصفحة الرئيسية.",
  },
  general: {
    org: "المؤسسة",
    orgHint: "المؤسسة التي تعمل فيها الآن.",
    name: "الاسم",
    yourRole: "دورك",
    members: "الأعضاء",
    plan: "الخطة",
    renameHint: "لتغيير اسم مؤسستك، تواصل مع منصة ملفك.",
    manageTeam: "إدارة الفريق",
    viewPlan: "عرض الخطة",
    calendar: "التقويم",
    calendarHint: "المناسبات والعطل والتذكيرات التي تظهر في تقويم المحتوى.",
  },
  aiPage: {
    accounts: "ردود الذكاء الاصطناعي لكل حساب",
    accountsHint: "عند التفعيل يقرأ الذكاء الاصطناعي كل تعليق ورسالة جديدة، ويضع لها وسماً ويقترح رداً. لا يُرسل شيء دونك أو دون قاعدة فعّلتها.",
    noAccounts: "اربط حساب إنستغرام لتفعيل ردود الذكاء الاصطناعي.",
    connect: "الانتقال إلى القنوات",
    tags: "الوسوم والفئات الذكية",
    more: "أدوات ذكاء اصطناعي أخرى",
    rules: "القواعد",
    rulesHint: "حدّد ما يحدث تلقائياً، ومتى يكتفي الذكاء الاصطناعي بالاقتراح.",
    agents: "الوكلاء الأذكياء",
    agentsHint: "شخصيات تردّ بأسلوبك، ولكلٍّ منها حدوده.",
  },
  team: {
    title: "الفريق والأدوار",
    hint: "من يستطيع المشاهدة والتصرّف في هذه المؤسسة.",
    invite: "دعوة زميل",
    inviteTitle: "دعوة زميل",
    inviteHint: "سيصله بريد فيه رابط للانضمام إلى {{org}}.",
    inviteEmail: "البريد الإلكتروني",
    inviteRole: "الدور",
    inviteSend: "إرسال الدعوة",
    inviteSent: "الدعوات قادمة قريباً. سجّلنا {{email}}.",
    member: "العضو",
    role: "الدور",
    joined: "تاريخ الانضمام",
    you: "أنت",
    ownerLocked: "لا يمكن تغيير دور المالك.",
    selfLocked: "لا يمكنك تغيير دورك بنفسك.",
    roleSoon: "تغيير الأدوار قادم قريباً.",
    count: "{{n}} أعضاء",
    roleHints: {
      owner: "كل شيء، بما في ذلك الفوترة وحذف المؤسسة.",
      admin: "إدارة الفريق والقنوات والأتمتة.",
      editor: "الرد والنشر وتعديل القواعد.",
      viewer: "قراءة فقط للوارد والتحليلات.",
    },
  },
  channels: {
    title: "القنوات",
    hint: "الحسابات المربوطة وتحديثاتها المباشرة وما تدعمه كل قناة.",
    connected: "الحسابات المربوطة",
    connect: "ربط إنستغرام",
    connecting: "جارٍ الربط…",
    connectedToast: "تم ربط إنستغرام",
    noAccounts: "لا توجد قنوات مربوطة بعد",
    noAccountsHint: "اربط إنستغرام لتصل التعليقات والرسائل إلى منصة ملفك.",
    status: "الحالة",
    webhook: "التحديثات المباشرة",
    subscribed: "مشترك",
    notSubscribed: "غير مشترك",
    fields: "الحقول",
    lastSync: "آخر مزامنة",
    never: "أبداً",
    connectedSince: "تاريخ الربط",
    followers: "المتابعون",
    syncNow: "زامِن الآن",
    syncing: "جارٍ مزامنة ‎@{{user}}…",
    synced: "‎@{{user}} محدّث",
    ai: "ردود الذكاء الاصطناعي",
    aiHint: "تصنيف التعليقات وصياغة الردود لهذا الحساب.",
    aiOn: "تم تفعيل الذكاء الاصطناعي لـ ‎@{{user}}",
    aiOff: "تم إيقاف الذكاء الاصطناعي لـ ‎@{{user}}",
    disconnect: "فصل",
    disconnectTitle: "فصل ‎@{{user}}؟",
    disconnectBody: "سيتوقف ملفك عن استقبال التعليقات والرسائل من هذا الحساب. يبقى السجل محفوظاً ويمكنك إعادة الربط متى شئت.",
    disconnected: "تم فصل ‎@{{user}}",
    reconnect: "إعادة الربط",
    matrix: "ما تستطيعه كل قناة",
    matrixHint: "يكيّف ملفك كل شاشة مع هذه الحدود، فلا يفشل أي إجراء عند المنصة.",
    capability: "الميزة",
    available: "متاح",
    comingSoon: "قريباً",
    rows: {
      publish: "النشر",
      carousel: "عناصر الألبوم",
      quota: "منشورات كل 24 ساعة",
      commentReply: "الرد على التعليقات",
      commentHide: "إخفاء التعليقات",
      privateReply: "رد خاص على تعليق",
      dm: "الرسائل المباشرة",
      dmWindow: "نافذة الرد الحر",
      outsideWindow: "بعد انتهاء النافذة",
      quickReplies: "ردود سريعة وأزرار",
      insights: "الإحصاءات",
    },
    outside: {
      none: "لا رسائل",
      message_tag: "وسوم الرسائل",
      template: "قوالب معتمدة",
    },
  },
  notifications: {
    title: "الإشعارات",
    hint: "اختر ما يصلك داخل منصة ملفك. تُحفظ على هذا الجهاز.",
    soon: "البريد وسلاك وتيليغرام قريباً.",
    event: "الحدث",
    saved: "تم حفظ تفضيلات الإشعارات",
    events: {
      approvals: "ردود بانتظار الموافقة",
      crisis: "ارتفاع التعليقات السلبية",
      newMessage: "رسالة مباشرة جديدة",
      assigned: "أُسند إليّ",
      publishFailed: "تعثّر نشر منشور",
      published: "نُشر منشور مجدول",
      batchDone: "اكتمل إجراء جماعي",
      weeklyReport: "تقرير الأداء الأسبوعي",
    },
    channels: {
      in_app: "داخل التطبيق",
      email: "البريد",
      slack: "سلاك",
      telegram: "تيليغرام",
    },
    connectHint: "تبدأ إشعارات سلاك وتيليغرام بعد ربط هذه التكاملات.",
  },
  appearance: {
    title: "المظهر",
    hint: "اجعل العمل في منصة ملفك مريحاً.",
    theme: "السمة",
    themeHint: "«حسب النظام» يتبع جهازك ويتحوّل للداكن ليلاً تلقائياً.",
    system: "حسب النظام",
    light: "فاتح",
    dark: "داكن",
    language: "اللغة",
    languageHint: "الواجهة كلها والتواريخ والأرقام تتبع هذا الاختيار.",
    density: "الكثافة",
    densityHint: "المضغوط يعرض صفوفاً أكثر في الوارد والجداول.",
    comfortable: "مريح",
    compact: "مضغوط",
    ask: "زر «اسأل»",
    askHint: "يفتح المساعد دائماً من «اسأل» في الشريط العلوي أو بالاختصار ⌘J.",
    floating: "إظهار زر «اسأل» عائم أيضاً",
    floatingHint: "زر دائري أسفل كل صفحة.",
  },
  api: {
    title: "الواجهة البرمجية والتكاملات",
    hint: "ابنِ على ملفك عبر الواجهة البرمجية العامة والإشعارات الصادرة.",
    keys: "مفاتيح الواجهة البرمجية",
    keysHint: "المفاتيح تتصرّف باسم المؤسسة. احفظها ككلمات المرور.",
    name: "الاسم",
    key: "المفتاح",
    created: "تاريخ الإنشاء",
    lastUsed: "آخر استخدام",
    createKey: "إنشاء مفتاح",
    soon: "الوصول للواجهة البرمجية العامة قادم قريباً.",
    sample: "مفتاح تجريبي (معطّل)",
    webhooks: "الإشعارات الصادرة",
    webhooksHint: "أرسل أحداث ملفك إلى Zapier أو Make أو n8n أو خادمك.",
    endpoint: "عنوان أحداث ملفك",
    copy: "نسخ",
    copied: "تم النسخ",
    docs: "مرجع الواجهة البرمجية",
  },
  knowledge: {
    title: "البحث الذكي ومعرفة المساعد",
    hint: "تُفهرس التعليقات والرسائل والتسميات التوضيحية بالمعنى للبحث الذكي والمساعد.",
    state: { ok: "سليم", degraded: "متأثر جزئيًا", unavailable: "غير متاح" },
    datasets: { comments: "التعليقات والرسائل والتسميات", brand: "مستندات العلامة التجارية" },
    indexed: "{{n}} مفهرس",
    indexing: "{{n}} قيد الفهرسة",
    failed: "{{n}} فشل",
    notChecked: "لم يُفحص بعد",
    failedTitle_zero: "مستندات تعذّرت فهرستها: {{count}}",
    failedTitle_one: "مستندات تعذّرت فهرستها: {{count}}",
    failedTitle_two: "مستندات تعذّرت فهرستها: {{count}}",
    failedTitle_few: "مستندات تعذّرت فهرستها: {{count}}",
    failedTitle_many: "مستندات تعذّرت فهرستها: {{count}}",
    failedTitle_other: "مستندات تعذّرت فهرستها: {{count}}",
    failedHint: "لا يمكن العثور عليها بالبحث الذكي أو المساعد. أعد الفهرسة لإرسالها من جديد.",
    lastError: "آخر خطأ: {{error}}",
    reindex: "إعادة الفهرسة",
    reindexStarted: "بدأت إعادة الفهرسة. تابعها في المهام.",
    adminOnly: "اطلب من المالك أو المسؤول إعادة الفهرسة.",
  },
};
